import React, { useState, useEffect } from 'react';
import { FlaskConical, CheckCircle, Send, MapPin, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Page dediee a l'inscription d'un LABORATOIRE (distincte de /clinic/register
// ET de /hopital/inscription) : un labo n'est ni une clinique ni un hopital,
// ni dans le vocabulaire ni dans l'espace qu'il obtient une fois inscrit.
// Poste sur /api/sih/establishments/register-laboratoire, qui cree directement
// un compte "establishment_member" + l'etablissement (establishment_type =
// "laboratory") + le rattachement gestionnaire — meme parcours que
// HopitalInscription.js pour un hopital, cote labo. Le compte arrive ensuite
// dans /hopital, ou HopitalDashboard.js n'affiche que les onglets pertinents
// pour un labo (voir LAB_MODULES).
const LaboRegister = () => {
  const [form, setForm] = useState({
    name: '',
    wilaya_code: '',
    address: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [wilayas, setWilayas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/wilayas`).then(res => setWilayas(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wilaya_code) {
      toast.error('Veuillez sélectionner votre wilaya');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        wilaya_code: form.wilaya_code,
        address: form.address,
        phone: form.phone,
        email: form.email,
        password: form.password,
      };
      await axios.post(`${API_URL}/api/sih/establishments/register-laboratoire`, payload);
      setSent(true);
      toast.success('Inscription réussie ! Vérifiez votre email.');
    } catch (error) {
      const msg = error.response?.data?.detail || "Erreur lors de l'envoi";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <section className="bg-gradient-to-r from-[#0596DE] to-[#047CBA] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
            <FlaskConical className="w-4 h-4" />
            <span className="text-sm font-medium">Inscription laboratoire</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">
            Inscrivez votre laboratoire sur Tabbibak
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Gérez votre personnel, votre file d'attente et vos prélèvements, et soyez trouvé par les patients qui cherchent un laboratoire près de chez eux.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm">
            {sent ? (
              <div className="text-center py-12">
                <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
                <h2 className="text-xl font-bold text-[#0F172A] mb-2">Inscription réussie !</h2>
                <p className="text-[#64748B] mb-6">
                  Un email de vérification a été envoyé à <strong>{form.email}</strong>. Cliquez sur le lien dans l'email pour activer votre compte. Notre équipe validera ensuite votre laboratoire sous 48h.
                </p>
                <Button onClick={() => window.location.href = '/'} className="rounded-full">
                  Retour à l'accueil
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="mb-1 block">Nom du laboratoire *</Label>
                  <Input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Ex: Laboratoire El Chifa"
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1 block">Wilaya *</Label>
                    <select
                      name="wilaya_code"
                      value={form.wilaya_code}
                      onChange={handleChange}
                      required
                      className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE] bg-white"
                    >
                      <option value="">Sélectionner la wilaya...</option>
                      {wilayas.map((w) => (
                        <option key={w.code} value={w.code}>{w.code} - {w.name_fr}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="mb-1 block">Téléphone *</Label>
                    <Input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+213 XX XX XX XX"
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-1 block">Adresse complète *</Label>
                  <Input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Ex: Rue Didouche Mourad, Alger Centre"
                    required
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <Label className="mb-1 block">Email professionnel *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="contact@laboratoire.dz"
                    required
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="mb-1 block">Mot de passe *</Label>
                    <Input
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={handleChange}
                      placeholder="Au moins 8 caractères"
                      required
                      minLength={8}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="mb-1 block">Confirmer le mot de passe *</Label>
                    <Input
                      name="confirmPassword"
                      type="password"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      placeholder="Retapez le mot de passe"
                      required
                      minLength={8}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-xl bg-[#0596DE] hover:bg-[#047CBA] h-12 text-base font-semibold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {loading ? (
                    <span className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer ma demande
                    </>
                  )}
                </button>

                <p className="text-xs text-center text-[#64748B] mt-4">
                  Votre demande sera examinée sous 48h. Vous recevrez vos identifiants de connexion par email après validation.
                </p>
              </form>
            )}

            <div className="mt-8 pt-6 border-t border-[#E2E8F0] grid md:grid-cols-2 gap-4 text-sm text-[#64748B]">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#0596DE]" />
                <span>contact@tabbibak.com</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#0596DE]" />
                <span>Algérie</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0F172A] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-bold text-xl text-[#0596DE]">Tabbibak.dz</span>
          <div className="border-t border-gray-800 mt-6 pt-6 text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.dz
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LaboRegister;
